import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Share2, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Convert dataURL → File so navigator.share() can carry the PNG.
const dataUrlToFile = async (dataUrl, filename) => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: "image/png" });
};

// Persist the snapshot to the user's history (Settings → Past snapshots).
// Best-effort — failures are silent so the share/download UX keeps working.
const persistSnapshot = async (file, kind, metrics) => {
  try {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    if (metrics) fd.append("metrics_json", JSON.stringify(metrics));
    await api.post("/snapshots", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  } catch (e) {
    // swallow — saving to timeline is non-critical
    console.warn("snapshot save failed", e);
  }
};

/**
 * Anonymized "social proof" snapshot of the coach's business pulse.
 * Renders a hidden 1080×1080 card off-screen, rasterizes it with html2canvas,
 * and either invokes the native share sheet (mobile) or downloads the PNG.
 */
export default function ShareSnapshot({ analytics, coachFirstName = "Coach" }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const capture = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0b0b0c",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL("image/png");
      const filename = `ripple-effect-snapshot-${new Date().toISOString().slice(0, 10)}.png`;
      const file = await dataUrlToFile(dataUrl, filename);

      // Save to the user's timeline (Settings → Past snapshots). Non-blocking.
      persistSnapshot(file, "coach", analytics);

      // Prefer the native share sheet when available (iOS/Android Chrome/Safari).
      if (
        navigator.canShare &&
        navigator.canShare({ files: [file] }) &&
        typeof navigator.share === "function"
      ) {
        try {
          await navigator.share({
            files: [file],
            title: "My Coach Scorecard — Ripple Effect",
            text: "30-day coaching scorecard. rippleprogress.com",
          });
          toast.success("Shared");
          return;
        } catch (err) {
          if (err?.name === "AbortError") return; // user dismissed sheet
          // Any other failure → fall through to download fallback
        }
      }

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Snapshot downloaded");
    } catch (e) {
      toast.error("Could not generate snapshot");
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  if (!analytics) return null;

  const a = analytics;

  return (
    <>
      <button
        data-testid="share-snapshot-btn"
        onClick={capture}
        disabled={busy}
        className="ghost-btn px-3 py-2 text-[11px] uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
        title="Download an anonymized PNG of your coaching stats"
      >
        {busy ? <Download className="h-3.5 w-3.5 animate-pulse" /> : <Share2 className="h-3.5 w-3.5" />}
        {busy ? "Rendering…" : "Share snapshot"}
      </button>

      {/* Off-screen render target. Kept in the DOM so html2canvas can rasterize
          it. position:fixed + left:-9999px keeps it out of the user's view. */}
      <div
        ref={cardRef}
        data-testid="share-snapshot-canvas"
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-9999px",
          top: 0,
          width: 1080,
          height: 1080,
          background: "#0b0b0c",
          color: "#e7e7e7",
          fontFamily: "Manrope, Arial, sans-serif",
          padding: 72,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 56 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src="/icon-192.png" alt="" width={56} height={56} style={{ borderRadius: 4 }} />
            <div>
              <div style={{ fontSize: 13, letterSpacing: 4, color: "#FF3B30", textTransform: "uppercase" }}>/ Ripple Effect</div>
              <div style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", fontSize: 44, lineHeight: 1, letterSpacing: 2, marginTop: 8 }}>
                COACH SCORECARD.
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, letterSpacing: 3, color: "#7a7a7d", textTransform: "uppercase" }}>
            {new Date().toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </div>
        </div>

        <div
          style={{
            fontSize: 22,
            color: "#b8b8bb",
            lineHeight: 1.35,
            marginBottom: 56,
            maxWidth: 820,
          }}
        >
          {coachFirstName ? `${coachFirstName}'s` : "My"} coaching business — at a glance.
          <span style={{ color: "#7a7a7d" }}> Real athletes. Real results.</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <Tile label="Athletes coached" value={a.total_clients ?? 0} sub="Linked roster" />
          <Tile
            label="14-day adherence"
            value={`${a.adherence_14d_pct ?? 0}%`}
            sub={`${a.adhered_count ?? 0} of ${a.total_clients ?? 0} logged a workout`}
            highlight
          />
          <Tile
            label="Weekly check-ins"
            value={`${a.checkins_done ?? 0}/${a.checkins_due ?? 0}`}
            sub="Completed vs due"
          />
          <Tile label="Active since" value={"24/7"} sub="On-call AI coaching" />
        </div>

        <div
          style={{
            marginTop: 72,
            paddingTop: 28,
            borderTop: "1px solid #2a2a2d",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: 16, color: "#b8b8bb" }}>
            Ready to train smarter?
            <span style={{ color: "#FF3B30", marginLeft: 12, fontWeight: 700 }}>rippleprogress.com</span>
          </div>
          <div style={{ fontSize: 11, color: "#7a7a7d", letterSpacing: 3, textTransform: "uppercase" }}>
            Powered by Ripple Effect
          </div>
        </div>
      </div>
    </>
  );
}

function Tile({ label, value, sub, highlight }) {
  return (
    <div
      style={{
        background: "#141416",
        border: "1px solid #2a2a2d",
        padding: 28,
        borderRadius: 4,
      }}
    >
      <div style={{ fontSize: 12, color: "#7a7a7d", letterSpacing: 3, textTransform: "uppercase", marginBottom: 14 }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 68,
          lineHeight: 1,
          color: highlight ? "#FF3B30" : "#ffffff",
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 13, color: "#7a7a7d", marginTop: 12, textTransform: "uppercase", letterSpacing: 2 }}>
        {sub}
      </div>
    </div>
  );
}
